import { useCallback, useEffect, useState, useContext, Fragment } from "react";
import useAppMain from "src/hooks/useAppMain";
import {
  CLIENT_ROUTES,
  CLIENT_TAB_LIST,
  groupByDate,
} from "src/utils/constants";
import { useHeaderContext } from "src/api/context/HeaderContext";
import { Button, Flex, Text, useMantineTheme, Loader } from "@mantine/core";
import { AiOutlinePlus, AiTwotoneCalendar } from "react-icons/ai";
import { formatDate, get7Days } from "src/utils/dateHelper";
import EventCard from "./EventCard";
import { MdFormatListBulleted } from "react-icons/md";
import {
  AttendanceByClientQuery,
  useAttendanceByClientLazyQuery,
} from "src/generated/graphql";
import { AppContext } from "src/api/AppContext";
import { useNavigate } from "react-router-dom";

export type IEvents = AttendanceByClientQuery["attendanceByClientUpcoming"];

function filterDataByDate(data: IEvents, targetDate: Date): IEvents {
  const filteredData = data.filter((item) => {
    const itemDate = new Date(item.meeting.startAt);
    const isSameDate =
      itemDate.getFullYear() === targetDate.getFullYear() &&
      itemDate.getMonth() === targetDate.getMonth() &&
      itemDate.getDate() === targetDate.getDate();

    return isSameDate;
  });

  return filteredData;
}

export default function MyCalendar() {
  const navigate = useNavigate();
  useAppMain(CLIENT_TAB_LIST.MY_CALENDAR, false);
  const { updateHeaderContent } = useHeaderContext();
  const theme = useMantineTheme();
  const { currentClient } = useContext(AppContext);
  const [isListView, setisListView] = useState(false);
  const [listDate, setlistDate] = useState(new Date());
  const [eventDetails, setEventDetails] = useState<IEvents[]>([]);

  useEffect(() => {
    updateHeaderContent(rightContent);
  }, [isListView]);

  const rightContent = useCallback(() => {
    return (
      <Flex gap={"sm"} justify={"center"} align={"center"}>
        {isListView ? (
          <MdFormatListBulleted
            size={25}
            onClick={() => setisListView(!isListView)}
            color={theme.colors.secondaryTextColor[6]}
          />
        ) : (
          <AiTwotoneCalendar
            size={25}
            onClick={() => setisListView(!isListView)}
            color={theme.colors.secondaryTextColor[6]}
          />
        )}
      </Flex>
    );
  }, [isListView, theme]);

  const [getAttendance, { data, loading }] = useAttendanceByClientLazyQuery({
    onCompleted: (data) => {
      setEventDetails(
        groupByDate(
          data.attendanceByClientUpcoming,
          (item) => item.meeting.startAt,
          "ASC"
        )
      );
    },
  });

  useEffect(() => {
    currentClient?.id &&
      getAttendance({ variables: { clientId: currentClient.id } });
  }, [currentClient]);

  const handleDateChange = (date: Date) => {
    setlistDate(date);
  };

  const listView = useCallback(() => {
    if (!data?.attendanceByClientUpcoming) return <></>;
    let arrayAttendance = filterDataByDate(
      data.attendanceByClientUpcoming,
      listDate
    );
    return (
      <>
        <Flex gap={"sm"} justify={"space-between"} mt={"lg"} mb={"xl"}>
          {get7Days()?.map((d) => (
            <Flex
              align={"center"}
              justify={"center"}
              direction={"column"}
              key={d.day}
              onClick={() => handleDateChange(d.date)}
            >
              <Text
                size={14}
                fw={600}
                color={theme.colors.secondaryTextColor[6]}
              >
                {d.day}
              </Text>

              <Text
                size={14}
                mt={5}
                sx={{
                  background:
                    listDate.getDate() === d.date.getDate()
                      ? theme.colors.primary[0]
                      : theme.colors.primary[6],
                  padding: 8,
                  borderRadius: 4,
                  color: theme.colors.primaryTextColor[6],
                }}
              >
                {d.date.getDate()}
              </Text>
            </Flex>
          ))}
        </Flex>

        {!arrayAttendance.length && (
          <Text
            align="center"
            fw={"bold"}
            size={"xl"}
            mt={"xl"}
            color={theme.colors.secondaryTextColor[6]}
          >
            No Event found
          </Text>
        )}

        {arrayAttendance.map((detail) => (
          <EventCard detail={detail} key={detail.id} />
        ))}
      </>
    );
  }, [listDate, data?.attendanceByClientUpcoming]);

  const handleScheduleEvent = () => {
    navigate("/" + CLIENT_ROUTES.SCHEDULE_LOCATION);
  };

  if (loading)
    return <Loader width={"100%"} mt={"xl"} sx={{ margin: "auto" }} />;
  return (
    <>
      <Button
        fullWidth
        leftIcon={<AiOutlinePlus />}
        onClick={handleScheduleEvent}
      >
        Schedule Event
      </Button>

      {!data?.attendanceByClientUpcoming.length && (
        <Text
          align="center"
          fw={"bold"}
          size={"xl"}
          mt={"xl"}
          color={theme.colors.secondaryTextColor[6]}
        >
          No Event found
        </Text>
      )}

      {isListView ? (
        listView()
      ) : (
        <>
          {eventDetails
            ?.filter((eventGroup) => {
              return (
                new Date(eventGroup[0].meeting.endAt).valueOf() >=
                new Date().valueOf()
              );
            })
            ?.map((event) => (
              <Fragment key={event[0].id}>
                <Text
                  size={14}
                  fw={600}
                  color={theme.colors.secondaryTextColor[6]}
                  mt={"md"}
                >
                  {formatDate(event[0].meeting.startAt).formatNumeric}
                </Text>
                {event?.map((detail) => (
                  <EventCard detail={detail} key={detail.id} />
                ))}
              </Fragment>
            ))}
        </>
      )}
    </>
  );
}
